import { useMemo, useEffect, useState } from "react";

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// react-table components
import { useTable, usePagination, useGlobalFilter, useAsyncDebounce, useSortBy } from "react-table";

// @mui material components
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { Autocomplete, Card, CardContent } from "@mui/material";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDPagination from "components/MDPagination";

// Material Dashboard 2 React example components
import DataTableHeadCell from "components/DataTable/DataTableHeadCell";
import DataTableBodyCell from "components/DataTable/DataTableBodyCell";
import MDButton from "components/MDButton";
import useLocale from "context/useLocale";
import { Link } from "react-router-dom";

function DataTable({
  entriesPerPage,
  canSearch,
  canAdd,
  showTotalEntries,
  table,
  pagination,
  isSorted,
  noEndBorder,
  paginationMeta,
  gotoPage,
  handleSortChange,
  handleSearchChange,
}) {
  const locale = useLocale();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [pageIndex, setPageIndex] = useState(0);
  const defaultValue = entriesPerPage.defaultValue ? entriesPerPage.defaultValue : 30;
  const entries = entriesPerPage.entries
    ? entriesPerPage.entries.map((el) => el.toString())
    : ["5", "10", "15", "20", "25"];
  const columns = useMemo(() => table.columns, [table]);
  const data = useMemo(() => table.rows, [table]);
  const totalCount = useMemo(
    () => (paginationMeta.total ? paginationMeta.total : 0),
    [paginationMeta]
  );

  const tableInstance = useTable(
    { columns, data, initialState: { pageIndex: 0 } },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    rows,
    page,
    setPageSize,
    setGlobalFilter,
    state: { pageSize, globalFilter },
  } = tableInstance;
  const pageOptions = useMemo(() => {
    const options = [];
    for (let i = 0; i < paginationMeta.total_pages; i += 1) {
      options.push(i);
    }
    return options;
  }, [paginationMeta]);
  const canNextPage = useMemo(() => pageIndex < pageOptions.length - 1, [pageIndex, pageOptions]);
  const canPreviousPage = useMemo(() => pageIndex > 0, [pageIndex]);
  const paginate = (p) => {
    setPageIndex(p);
    gotoPage(p + 1);
  };
  const nextPage = () => paginate(pageIndex + 1);
  const previousPage = () => paginate(pageIndex - 1);

  // Set the default value for the entries per page when component mounts
  useEffect(() => setPageSize(defaultValue || 15), [defaultValue]);

  // Set the entries per page value based on the select value
  const setEntriesPerPage = (value) => setPageSize(value);

  // Render the paginations
  const renderPagination = pageOptions.map((option) => (
    <MDPagination
      item
      key={option}
      onClick={() => paginate(Number(option))}
      active={pageIndex === option}
    >
      {option + 1}
    </MDPagination>
  ));

  // Handler for the input to set the pagination index
  const handleInputPagination = ({ target: { value } }) =>
    value > pageOptions.length || value < 0 ? paginate(0) : paginate(Number(value));

  // Customized page options starting from 1
  const customizedPageOptions = pageOptions.map((option) => option + 1);

  // Setting value for the pagination input
  const handleInputPaginationValue = ({ target: value }) => paginate(Number(value.value - 1));

  // Search input value state
  const [search, setSearch] = useState(globalFilter);

  // Search input state handle
  const onSearchChange = useAsyncDebounce((value) => {
    setGlobalFilter(value || undefined);
  }, 100);

  // A function that sets the sorted value for the table
  const setSortedValue = (column) => {
    let sortedValue;

    if (isSorted && column.isSorted) {
      sortedValue = column.isSortedDesc ? "desc" : "asce";
    } else if (isSorted) {
      sortedValue = "none";
    } else {
      sortedValue = false;
    }

    return sortedValue;
  };

  // Setting the entries starting point
  const entriesStart =
    pageIndex === 0 ? pageIndex + (totalCount >= 1 ? 1 : 0) : pageIndex * pageSize + 1;

  // Setting the entries ending point
  let entriesEnd;

  if (pageIndex === 0) {
    entriesEnd = pageSize > totalCount ? totalCount : pageSize;
  } else if (pageIndex === pageOptions.length - 1) {
    entriesEnd = totalCount;
  } else {
    entriesEnd = pageSize * (pageIndex + 1);
  }

  // Render search inputs for searchable columns
  // TODO: Add debounce on search change
  const renderSearchInputs = (
    <MDBox display="flex" flexWrap="wrap">
      {columns
        .filter((column) => column.searchable)
        .map((column, columnIndex) => {
          const columnKey = column.id || column.accessor || column.Header || columnIndex;
          return (
            <MDBox key={columnKey} pt={3} px={2} width="50%">
              <MDTypography variant="caption" color="secondary">
                {locale("search")} {column.Header}
              </MDTypography>
              <MDInput
                placeholder={`${locale("search")} ${column.Header}...`}
                size="small"
                fullWidth
                onChange={handleSearchChange(column.accessor)}
              />
            </MDBox>
          );
        })}
    </MDBox>
  );

  return (
    /* eslint-disable react/jsx-props-no-spreading */
    <MDBox>
      <MDBox component="div" sx={{ boxShadow: "none", overflowX: "auto" }}>
        {renderSearchInputs}
        {(entriesPerPage || canSearch) && (
          <MDBox display="flex" justifyContent="space-between" alignItems="center" p={3}>
            {entriesPerPage && (
              <MDBox display="flex" alignItems="center">
                <Autocomplete
                  disableClearable
                  value={pageSize.toString()}
                  options={entries}
                  onChange={(event, newValue) => {
                    setEntriesPerPage(parseInt(newValue, 10));
                  }}
                  size="small"
                  sx={{ width: "5rem" }}
                  renderInput={(params) => <MDInput {...params} />}
                />
                <MDTypography variant="caption" color="secondary">
                  &nbsp;&nbsp;{locale("entriesPerPage")}
                </MDTypography>
              </MDBox>
            )}
            {canSearch && (
              <MDBox width="12rem" ml="auto">
                <MDInput
                  placeholder={`${locale("search")}...`}
                  value={search}
                  size="small"
                  fullWidth
                  onChange={({ currentTarget }) => {
                    setSearch(currentTarget.value);
                    onSearchChange(currentTarget.value);
                  }}
                />
              </MDBox>
            )}
            {canAdd && (
              <MDButton variant="gradient" component={Link} color="success" to="new">
                <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                &nbsp;{locale("addNew")}
              </MDButton>
            )}
          </MDBox>
        )}
        {isMobile ? (
          <MDBox p={2}>
            {page.map((row, key) => {
              prepareRow(row);
              return (
                <Card key={row.id || key} sx={{ mb: 2 }}>
                  <CardContent>
                    {row.cells.map((cell, cellIndex) => {
                      const cellKey =
                        cell.column.id || cell.column.accessor || cell.column.Header || cellIndex;
                      return (
                        <MDTypography key={cellKey} variant="body2">
                          <strong>{cell.column.render("Header")}: </strong>
                          {cell.render("Cell")}
                        </MDTypography>
                      );
                    })}
                  </CardContent>
                </Card>
              );
            })}
          </MDBox>
        ) : (
          <table {...getTableProps()} style={{ width: "100%" }}>
            <MDBox component="thead">
              {headerGroups.map((headerGroup) => (
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column) => (
                    <DataTableHeadCell
                      {...column.getHeaderProps(isSorted && column.getSortByToggleProps())}
                      width={column.width ? column.width : "auto"}
                      align={column.align ? column.align : "left"}
                      sorted={setSortedValue(column)}
                      onClick={() => {
                        handleSortChange(column.id);
                        setPageIndex(0);
                      }}
                    >
                      {column.render("Header")}
                    </DataTableHeadCell>
                  ))}
                </tr>
              ))}
            </MDBox>
            <tbody {...getTableBodyProps()}>
              {page.map((row, key) => {
                prepareRow(row);
                return (
                  <tr {...row.getRowProps()}>
                    {row.cells.map((cell, cellIndex) => {
                      const cellKey =
                        cell.column.id || cell.column.accessor || cell.column.Header || cellIndex;
                      return (
                        <DataTableBodyCell
                          key={cellKey}
                          noBorder={noEndBorder && rows.length - 1 === key}
                          align={cell.column.align ? cell.column.align : "left"}
                          {...cell.getCellProps()}
                        >
                          {cell.render("Cell")}
                        </DataTableBodyCell>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        <MDBox
          display="flex"
          flexDirection={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          p={!showTotalEntries && pageOptions.length === 1 ? 0 : 3}
        >
          {showTotalEntries && (
            <MDBox mb={{ xs: 3, sm: 0 }}>
              <MDTypography variant="button" color="secondary" fontWeight="regular">
                {locale("showing")} {entriesStart} to {entriesEnd} of {totalCount}{" "}
                {locale("entries")}
              </MDTypography>
            </MDBox>
          )}
          {pageOptions.length > 1 && (
            <MDPagination
              variant={pagination.variant || "gradient"}
              color={pagination.color || "info"}
            >
              {canPreviousPage && (
                <MDPagination item onClick={() => previousPage()}>
                  <Icon sx={{ fontWeight: "bold" }}>chevron_left</Icon>
                </MDPagination>
              )}
              {renderPagination.length > 6 ? (
                <MDBox width="5rem" mx={1}>
                  <MDInput
                    inputProps={{ type: "number", min: 1, max: customizedPageOptions.length }}
                    value={customizedPageOptions[pageIndex]}
                    onChange={(handleInputPagination, handleInputPaginationValue)}
                  />
                </MDBox>
              ) : (
                renderPagination
              )}
              {canNextPage && (
                <MDPagination item onClick={() => nextPage()}>
                  <Icon sx={{ fontWeight: "bold" }}>chevron_right</Icon>
                </MDPagination>
              )}
            </MDPagination>
          )}
        </MDBox>
      </MDBox>
    </MDBox>
    /* eslint-disable react/jsx-props-no-spreading */
  );
}

// Setting default values for the props of DataTable
DataTable.defaultProps = {
  entriesPerPage: { defaultValue: 30, entries: [5, 10, 15, 20, 25] },
  canSearch: false,
  canAdd: true,
  showTotalEntries: true,
  pagination: { variant: "gradient", color: "info" },
  isSorted: true,
  noEndBorder: false,
  handleSortChange: () => {},
  handleSearchChange: () => {},
};

// Typechecking props for the DataTable
DataTable.propTypes = {
  entriesPerPage: PropTypes.oneOfType([
    PropTypes.shape({
      defaultValue: PropTypes.number,
      entries: PropTypes.arrayOf(PropTypes.number),
    }),
    PropTypes.bool,
  ]),
  canSearch: PropTypes.bool,
  canAdd: PropTypes.bool,
  showTotalEntries: PropTypes.bool,
  table: PropTypes.objectOf(PropTypes.array).isRequired,
  pagination: PropTypes.shape({
    variant: PropTypes.oneOf(["contained", "gradient"]),
    color: PropTypes.oneOf([
      "primary",
      "secondary",
      "info",
      "success",
      "warning",
      "error",
      "dark",
      "light",
    ]),
  }),
  isSorted: PropTypes.bool,
  noEndBorder: PropTypes.bool,
  paginationMeta: PropTypes.shape({
    total: PropTypes.number,
    current_page: PropTypes.number,
    total_pages: PropTypes.number,
  }).isRequired,
  gotoPage: PropTypes.func.isRequired,
  handleSortChange: PropTypes.func,
  handleSearchChange: PropTypes.func,
};

export default DataTable;
